import React from "react";
import { motion } from "framer-motion";
import { Database, Zap, MessageSquare, PhoneCall, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { fadeIn, fadeInUp, fadeInUpSmall } from "../../lib/animations";

const steps = [
  {
    step: "1",
    icon: Database,
    title: "Connect Your MLS",
    desc: "Link your MLS account once. We'll sync all your active listings automatically.",
  },
  {
    step: "2",
    icon: Zap,
    title: "AI Creates & Launches Ads",
    desc: "Our AI writes compelling ad copy and launches campaigns on Facebook & Instagram.",
  },
  {
    step: "3",
    icon: MessageSquare,
    title: "Buyer Leads Respond",
    desc: "Interested buyers see your ads and submit their contact information instantly.",
  },
  {
    step: "4",
    icon: PhoneCall,
    title: "AI Follows Up & Books",
    desc: "Our Voice AI calls leads within 60 seconds and schedules qualified showings.",
  },
];

export function HowItWorksSection({ navigate }) {
  return (
    <section className="relative z-10 px-6 md:px-12 lg:px-24 py-20 bg-white">
      <div className="max-w-5xl mx-auto">
        {/* Heading */}
        <motion.div
          initial={fadeInUpSmall.initial}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">How It Works</h2>
          <p className="text-gray-500 text-base md:text-lg">Four simple steps from listing to qualified buyer</p>
        </motion.div>

        {/* Steps row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 relative">
          {/* Connecting line (desktop only) */}
          <div className="hidden md:block absolute top-[72px] left-[12%] right-[12%] h-[3px]">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ delay: 1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-[#39FF14] via-[#39FF14] to-[#39FF14] origin-left rounded-full"
            />
          </div>

          {steps.map((item, i) => (
            <motion.div
              key={item.step}
              initial={fadeInUpSmall.initial}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ delay: 0.1 * i, duration: 0.4 }}
              className="flex flex-col items-center text-center relative"
            >
              {/* Icon box with badge */}
              <div className="relative mb-6">
                {/* Step number badge */}
                <motion.div
                  initial={{ scale: 0.8 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.05 }}
                  transition={{ delay: 0.1 + 0.1 * i, type: "spring", stiffness: 300 }}
                  className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold z-10"
                >
                  {item.step}
                </motion.div>

                {/* Icon container */}
                <motion.div
                  whileHover={{ scale: 1.08, rotate: 2 }}
                  transition={{ type: "spring", stiffness: 400 }}
                  className="w-20 h-20 rounded-2xl bg-[#39FF14]/10 flex items-center justify-center"
                >
                  <item.icon className="w-8 h-8 text-[#39FF14]" strokeWidth={1.8} />
                </motion.div>
              </div>

              {/* Title */}
              <h3 className="font-bold text-gray-900 mb-2 text-sm md:text-base">{item.title}</h3>

              {/* Description */}
              <p className="text-gray-500 text-xs md:text-sm leading-relaxed max-w-[200px]">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button
            onClick={() => navigate('/pricing')}
            className="bg-[#39FF14] text-black hover:bg-[#32E012] font-semibold px-8 py-5 text-base h-auto shadow-md"
            data-testid="how-it-works-cta"
          >
            See Plans & Pricing <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-gray-400 text-sm mt-3">7-day free trial on Market Leader. Cancel anytime.</p>
        </div>
      </div>
    </section>
  );
}
